import React from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import styles from './AccountantTaxAuthoritySection.module.scss';

const formatRemaining = (days, seconds, t) => {
  if (days > 0) return { value: days, unit: t('Days') };

  if (seconds > 0) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);

    if (h > 0) return { value: `${h}${m > 0 ? `:${String(m).padStart(2, '0')}` : ''}`, unit: t('Hours') };
    if (m > 0) return { value: m, unit: t('Minutes') };
    return { value: '<1', unit: t('Minute') };
  }

  return null;
};

const getScopeIcon = (scope) => {
  switch (scope) {
    case 'VatReport':
      return <DescriptionOutlinedIcon />;
    case 'AdvancesPayment':
      return <BarChartOutlinedIcon />;
    case 'ConfirmationInformation':
      return <ShieldOutlinedIcon />;
    case 'Annex1214B':
      return <FolderOutlinedIcon />;
    default:
      return <CheckCircleOutlineIcon />;
  }
};

const AccountantTaxAuthorityValidityCell = ({
  scope,
  tokenDaysRemaining,
  tokenSecondsRemaining,
  t,
  accountant,
  TaxAuthorityService,
  token,
  handleTaxAuthorityAuth,
  setLoading,
  refreshTokenValidity,
}) => {
  const stillValid = tokenSecondsRemaining > 0 || tokenDaysRemaining > 0;

  // Must match tokens stored after OAuth (appsettings confirmation_information_scope = invoices_scope).
  // Legacy accountant tokens may still use "scope" — delete clears both for ConfirmationInformation.
  const getBackendScope = (frontendScope) => {
    switch (frontendScope) {
      case 'VatReport':
        return 'vat_report_scope';
      case 'AdvancesPayment':
        return 'advances_payment_scope';
      case 'ConfirmationInformation':
        return 'invoices_scope';
      case 'Annex1214B':
        return 'annexes_scope';
      default:
        return frontendScope;
    }
  };

  const getScopeDisplayName = (scopeName) => {
    switch (scopeName) {
      case 'VatReport':
        return t('VATReport');
      case 'AdvancesPayment':
        return t('AdvancesPayment');
      case 'ConfirmationInformation':
        return t('ConfirmationInformation');
      case 'Annex1214B':
        return t('Annex1214B_1281_1362Scope');
      default:
        return scopeName;
    }
  };

  const remaining = formatRemaining(tokenDaysRemaining, tokenSecondsRemaining, t);
  const isLate = !stillValid;

  const handleReset = async () => {
    const { isConfirmed } = await Swal.fire({
      title: t('AreYouSure'),
      text: t('AreYouSureResetTaxAuthorityToken') || 'Reset Tax Authority login for this scope? You will need to re‑authenticate.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('Yes') || 'Yes',
      cancelButtonText: t('Cancel') || 'Cancel',
      reverseButtons: true,
    });
    if (!isConfirmed) return;
    setLoading(true);
    try {
      await TaxAuthorityService.deleteAccountantToken(accountant.accountantId, getBackendScope(scope), token);
      // Clear legacy ConfirmationInformation token key if present
      if (scope === 'ConfirmationInformation') {
        try {
          await TaxAuthorityService.deleteAccountantToken(accountant.accountantId, 'scope', token);
        } catch (_) { /* ignore if no legacy token */ }
      }
      await refreshTokenValidity?.(scope);
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async () => {
    setLoading(true);
    try {
      const tokenData = await TaxAuthorityService.requestTokenAccountant(
        accountant.accountantId,
        scope,
        token
      );
      if (tokenData?.authUrl) {
        handleTaxAuthorityAuth(tokenData.authUrl);
      } else if (tokenData?.accessToken) {
        await refreshTokenValidity?.(scope);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <div className={styles.scopeIcon}>{getScopeIcon(scope)}</div>
        {isLate ? (
          <span className={styles.badgeLate}>{t('Late', { defaultValue: 'איחור' })}</span>
        ) : (
          <span className={styles.statusCheck}>
            <CheckIcon />
          </span>
        )}
      </div>

      <h4 className={styles.scopeTitle}>{getScopeDisplayName(scope)}</h4>

      {stillValid ? (
        <div className={styles.validRow}>
          <div className={styles.days}>
            {remaining?.value}
            <span className={styles.daysMuted}>{remaining?.unit}</span>
          </div>
          <button type="button" className={styles.resetBtn} onClick={handleReset}>
            {t('Reset')}
          </button>
        </div>
      ) : (
        <button type="button" className={styles.requestBtn} onClick={handleRequest}>
          <ChevronLeftIcon sx={{ fontSize: 20, color: '#fff' }} />
          <span className={styles.requestBtnLabel}>{t('RequestRenewTaxAuthorityLogin')}</span>
        </button>
      )}
    </div>
  );
};

AccountantTaxAuthorityValidityCell.propTypes = {
  scope: PropTypes.string.isRequired,
  tokenDaysRemaining: PropTypes.number.isRequired,
  tokenSecondsRemaining: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  accountant: PropTypes.object.isRequired,
  TaxAuthorityService: PropTypes.object.isRequired,
  token: PropTypes.string,
  handleTaxAuthorityAuth: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  refreshTokenValidity: PropTypes.func.isRequired,
};

export default AccountantTaxAuthorityValidityCell;
